import React, { useState, useRef, useEffect, useCallback } from 'react';
// import { useTranslation } from "react-i18next";
import { Col, Row } from "react-bootstrap";
import { motion } from "framer-motion";
import Table from 'react-bootstrap/Table';

import SkeletonLoader from "../../../../../components/ui/SkeletonLoader";
import Pagination from "../../../../../components/ui/Pagination";
import NoDataFound from "../../../../../components/ui/NoDataFound";
import CustomTooltip from "../../../../../components/ui/Tooltip";

import EndpointsFilter from "../../../../../components/PostLogin/ScanInfo/ExposureManagement/ConcerningExposure/EndpointFilter";

import { searchIcon } from "../../../../../utils/icons";
import { formatDate, formatNumber } from "../../../../../utils/util";

import { useScanInfoContext } from "../../../../ScanInfo/ScanInfoContext";

import apiEndpoints from "../../../../../apiEndpoints";

import { useSelector, useDispatch } from 'react-redux';
import { getThirdPartyRiskStats, resetThirdPartyRiskStats, getCVEsList, resetCvesList } from "../../../../../store/actions/thirdPartyRiskAction";

const CriticalVulnerabilities = () => {

    const cvesStatsResponse = useSelector((state) => state.thirdPartyRisk?.thirdPartyRiskStats);
    const cvesListResponse = useSelector((state) => state?.thirdPartyRisk?.cvesList)

    const prevDomainIdRef = useRef(null);
    const prevScanIdRef = useRef(null);
    const dispatch = useDispatch();

    const { domainId, scanId, organizationId } = useScanInfoContext();

    const [searchValue, setSearchValue] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearchFocused, setIsSearchFocused] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(10);
    const [isLoading, setIsLoading] = useState(false);
    const [cvesList, setCVEsList] = useState(null);
    const [scanNotFound, setScanNotFound] = useState(false);

    const [cvesStats, setCVEsStats] = useState("");
    const [statsLoading, setStatsLoading] = useState(false);

    //selected filter value
    const [listFilterValue, setListFilterValue] = useState({});

    const endPointFilterApply = (data) => {
        setCurrentPage(1)
        setListFilterValue(data)
    }

    const isAnyEndpointFilterActive = Object.values(listFilterValue).some(value => Boolean(value));

    const resetEndpointFilterValue = () => {
        setCurrentPage(1)
        setListFilterValue({})
    }

    const setCurrentPageandRecordSize = (currentPage, pageSize) => {
        setCurrentPage(currentPage);
        if(pageSize !== currentPageSize) {
            setCurrentPageSize(pageSize);
            setCurrentPage(1);
        }
    }

    const handleSearchKeyDown = (event) => {
        if (event.key === "Enter" && isSearchFocused) {
            onSearch()
        }
    }

    const onSearch = () => {
        setCurrentPage(1)
        setSearchQuery(searchValue)
    }
            
    const fetchCriticalVunerabilitiesStats = useCallback(()=>{
        setStatsLoading(true)
        const baseUrl = `${apiEndpoints.scanInfo.thirdPartyRisk.riskAssessment.criticalVulnerabilities.cvesStats()}`
        const params = organizationId.toLowerCase() !== "all" && domainId.toLowerCase() === "all" ? `&organization_id=${organizationId}`: `&domain=${domainId}&scan_id=${scanId}`
        const url = `${baseUrl}${params}`
        dispatch(getThirdPartyRiskStats(url))
    },[dispatch, domainId, scanId, organizationId])

    const fetchCVEsList = useCallback(() => {
        setIsLoading(true)
        const baseUrl = `&page=${currentPage}&page_size=${currentPageSize}`;
        const params = organizationId.toLowerCase() !== "all" && domainId.toLowerCase() === "all" ? `&organization_id=${organizationId}`: `&domain=${domainId}&scan_id=${scanId}`
        const queryParams = searchQuery ? `&query=${searchQuery}` : "";
        const newFindings = listFilterValue?.newFindings?.value && listFilterValue?.newFindings?.value !== "all" ? `&new_record=${listFilterValue?.newFindings?.value === "yes" ? true : false}` : "";
        const status = listFilterValue?.status?.value && listFilterValue?.status?.value !== "all" ? `&status=${listFilterValue?.status?.value}` : "";
        const severity = listFilterValue?.severity?.value && listFilterValue?.severity?.value !== "all" ? `&severity=${listFilterValue?.severity?.value}` : "";
        const url = `${baseUrl}${params}${queryParams}${newFindings}${status}${severity}`
        dispatch(getCVEsList(url))
    },[currentPage, currentPageSize, domainId, organizationId, scanId, searchQuery, listFilterValue, dispatch])

    useEffect(()=>{
        fetchCVEsList()
    },[currentPage, fetchCVEsList])

    useEffect(()=>{
        if(domainId !== prevDomainIdRef.current || scanId !== prevScanIdRef.current) {
            fetchCriticalVunerabilitiesStats()
            setCurrentPage(1);
            prevDomainIdRef.current = domainId;
            prevScanIdRef.current = scanId;
        }
    },[domainId, scanId, fetchCriticalVunerabilitiesStats])

    useEffect(()=>{        
        if(cvesStatsResponse !== null) {
            setCVEsStats(cvesStatsResponse)
            dispatch(resetThirdPartyRiskStats())
            setStatsLoading(false)
        }
    },[cvesStatsResponse, dispatch])

    useEffect(()=>{
        if(cvesListResponse !== null) { 
            setCVEsList(cvesListResponse)
            if(cvesListResponse?.status === "failed") {
                setScanNotFound(true)
            } else {
                setScanNotFound(false)
            }
            dispatch(resetCvesList())
            setIsLoading(false)
        }
    },[cvesListResponse, dispatch])

    return (
        <>
            <div className="scan-info-main-content sensitive-exposure-main-content">
                <div className="bg-white-with-shadow p-4">
                    <Row>
                        <Col lg={4}>
                            <div className="discover-doamin-container associated-doamin-page rounded p-3 h-100">
                                {
                                    statsLoading ?
                                    <SkeletonLoader type={"line"} count={3} currentPage={"cves"} />
                                    :
                                    <motion.div
                                        className="h-100"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4 }}
                                    >
                                        
                                            <div className="header-content d-flex justify-content-between">
                                                <p className="heading">CVEs</p>
                                            </div>
                                            <div className="total-score-container rounded">
                                                <div className="users-totol-count d-flex">
                                                    <p className="total-score-count">{formatNumber(cvesStats?.stats?.record_type?.total)}</p>
                                                    <div className="score-count mx-3">
                                                        <span className="new-record d-inline-block mx-1"></span>
                                                        <span className="existing-record d-inline-block"></span>
                                                        <p>Total Records</p>
                                                    </div>
                                                </div>
                                                <div className="new-exist-count d-flex gap-5 mt-3">
                                                    <p className="new-record"><span className="d-inline-block">{formatNumber(cvesStats?.stats?.record_type?.new_record)}</span> New</p>
                                                    <p className="existing-record"><span className="d-inline-block">{formatNumber(cvesStats?.stats?.record_type?.existing_record)}</span> Existing</p>
                                                </div>
                                            </div>
                                    </motion.div>
                                }
                            </div>                            
                        </Col>
                        <Col lg={4}>
                            <div className="discover-doamin-container associated-doamin-page rounded p-3 h-100">
                                {
                                    statsLoading ?
                                    <SkeletonLoader type={"line"} count={3} currentPage={"cves"} />
                                    :
                                    <motion.div
                                        className="h-100"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4 }}
                                    >
                                        
                                            <div className="header-content d-flex justify-content-between align-items-center">
                                                <p className="heading">Severity</p>
                                                {/* <p className="view-all" onClick={()=>{
                                                    setViewAllType("severity")
                                                    setShowViewAllModal(true)}}
                                                >View all</p> */}
                                            </div>
                                            <div className="total-score-container rounded">
                                                <div className="users-totol-count d-flex align-items-baseline">
                                                    <p className="total-score-count">{formatNumber(cvesStats?.stats?.severity_type?.total)}</p>
                                                    <div className="score-count mx-3">
                                                        <p>Total Risk</p>
                                                    </div>
                                                </div>
                                                <div className="new-exist-count d-flex align-items-center gap-5 mt-3">
                                                    <p className="severity critical"><span className="d-inline-block">{formatNumber(cvesStats?.stats?.severity_type?.critical)}</span> Critical</p>
                                                    <p className="severity high"><span className="d-inline-block">{formatNumber(cvesStats?.stats?.severity_type?.high)}</span> High</p> 
                                                </div>
                                                <div className="new-exist-count d-flex align-items-center gap-5 mt-3">                                            
                                                    <p className="severity medium"><span className="d-inline-block">{formatNumber(cvesStats?.stats?.severity_type?.medium)}</span> Medium</p>
                                                    <p className="severity low"><span className="d-inline-block">{formatNumber(cvesStats?.stats?.severity_type?.low)}</span> Low</p>
                                                </div>
                                            </div>
                                    </motion.div>
                                }
                            </div>                           
                        </Col>
                        <Col lg={4}>
                            <div className="discover-doamin-container associated-doamin-page rounded p-3 h-100">
                                {
                                    statsLoading ?
                                    <SkeletonLoader type={"line"} count={3} currentPage={"cves"} />
                                    :
                                    <motion.div
                                        className="h-100"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4 }}
                                    >
                                        
                                            <div className="header-content d-flex justify-content-between">
                                                <p className="heading">Status</p>
                                            </div>
                                            <div className="total-score-container rounded">
                                                <div className="users-totol-count d-flex">
                                                    <p className="total-score-count">{formatNumber(cvesStats?.stats?.status_type?.total)}</p>
                                                    <div className="score-count mx-3">
                                                        <span className="new-record open d-inline-block mx-1"></span>
                                                        <span className="existing-record closed d-inline-block"></span>
                                                        <span className="existing-record informational d-inline-block mx-1"></span>
                                                        <p>Total Records</p>
                                                    </div>
                                                </div>
                                                <div className="new-exist-count d-flex gap-5 mt-3">
                                                    <p className="new-record open"><span className="d-inline-block">{formatNumber(cvesStats?.stats?.status_type?.open)}</span> Open</p>
                                                    <p className="existing-record closed"><span className="d-inline-block">{formatNumber(cvesStats?.stats?.status_type?.closed)}</span> Closed</p>
                                                </div>
                                                <div className="new-exist-count d-flex gap-5 mt-3">
                                                    <p className="existing-record informational"><span className="d-inline-block">{formatNumber(cvesStats?.stats?.status_type?.wont_fix)}</span> Won't Fix</p>
                                                </div>
                                            </div>
                                    </motion.div>
                                }
                            </div>                            
                        </Col>
                        {/* <Col lg={3}>
                            {
                                statsLoading ?
                                <SkeletonLoader type={"line"} count={3} currentPage={"cves"} />
                                :
                                <div className="sub-domain-type-container rounded p-3 h-100">
                                    <div className="header-content d-flex justify-content-between">
                                        <p className="heading">CVEs Type</p>
                                        {cvesStats !== "" && cvesStats?.stats?.cve_type?.records && Object.entries(cvesStats?.stats?.cve_type?.records).length > 4 && <p className="view-all" onClick={()=>{
                                            setViewAllType("type")
                                            setShowViewAllModal(true)
                                        }}>View all</p>}
                                    </div>
                                    <div className="total-records-container">
                                        <p className="content">{formatNumber(cvesStats?.stats?.cve_type?.total)} <span>Total Records</span></p>
                                    </div>
                                    <Row className="mt-3">
                                        {
                                            cvesStats !== "" && cvesStats?.stats?.cve_type?.records && Object.entries(cvesStats?.stats?.cve_type?.records).length > 0 &&
                                            Object.entries(cvesStats?.stats?.cve_type?.records).map(([key, value], index) => {
                                                if(index <= 3) {
                                                    return (
                                                        <Col lg={6} key={index}>
                                                            <p className="count-type sensitive-expo-count mt-2">
                                                            {formatNumber(value)} <span>{key}</span>
                                                            </p>
                                                        </Col>
                                                    )
                                                }
                                                return null;
                                            })
                                        }
                                    </Row>
                                </div>
                            }                            
                        </Col> */}
                    </Row>
                </div>
                <div className="domain-similarities-container git-leaks-main-content bg-white-with-shadow p-3 mt-3">
                    <div className="search-header-container d-flex align-items-center gap-3">
                        <div className="d-flex gap-3">
                            <div className="search-container position-relative" onKeyDown={handleSearchKeyDown}>
                                <span className="position-absolute">{searchIcon()}</span>
                                <input type="text" 
                                    placeholder="Search"
                                    value={searchValue} 
                                    onChange={(e)=>{
                                        setSearchValue(e.target.value)
                                    }}
                                    onFocus={() => setIsSearchFocused(true)}
                                    onBlur={() => setIsSearchFocused(false)}
                                />
                            </div>
                            <button className="search-btn" onClick={onSearch}>Search</button>
                        </div> 
                        <EndpointsFilter endPointFilterApply={endPointFilterApply} endpointFiltersValue={listFilterValue} currentPage={"criticalVulnerabilities"} />
                        {isAnyEndpointFilterActive && (<span className="reset-filter-link" onClick={resetEndpointFilterValue}>Reset Filter</span>)}
                    </div>
                    <div className="table-container js-secrets-table mt-4">
                        {
                            isLoading ?
                            <SkeletonLoader type={"line"} count={5} currentPage={"cvesList"} />
                            :
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Severity</th>
                                        <th>Description</th>
                                        <th>Endpoints</th>
                                        <th>Product/Version</th>
                                        <th>First / Last Seen</th>
                                        <th>Status</th>
                                    </tr>                                
                                </thead>
                                <tbody>
                                    {
                                        cvesList?.results?.length > 0 && 
                                        cvesList?.results.map((data, index)=>{
                                            return (
                                                <motion.tr
                                                    key={index}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.4, delay: index * 0.1 }}
                                                >
                                                    <td>
                                                        <p className="dns-record-subdomain">{data?.name} {data?.new_record && <span className="d-inline-block new-data mr-2">New</span>}</p>
                                                        <span className="dns-record-domain">{data?.domain}</span>
                                                    </td>
                                                    <td className="severity-container">
                                                        <p className={`severity ${data?.severity}`}>{data?.severity}</p>
                                                    </td>
                                                    <td className="cves-table-description">                                                       
                                                        {data?.description !== null ? <span className="author-name">{
                                                            data?.description.length > 120 ?
                                                            <CustomTooltip content={data?.description} position={"top"}>
                                                                {data?.description.slice(0, 120) + "..." }
                                                            </CustomTooltip>
                                                            : data?.description
                                                        }</span> : "-"}                                                        
                                                    </td>
                                                    <td>
                                                        <p className="dns-record-subdomain">Host: {data?.host}</p>
                                                        <span className="dns-record-subdomain">Port: {data?.port}</span>
                                                    </td>
                                                    <td>
                                                        <p className="dns-record-subdomain">Product: {data?.product}</p>
                                                        <span className="dns-record-subdomain">Version: {data?.version}</span>
                                                    </td>
                                                    <td className="first-seen-container">
                                                        <h4 className="verified-details m-0">First Seen:</h4>
                                                        <p className="value mb-2">{formatDate(data?.first_seen)}</p>
                                                        <h4 className="verified-details m-0">Last Seen:</h4>
                                                        <p className="value">{formatDate(data?.last_seen)}</p>
                                                    </td>
                                                    <td className="status-container">
                                                        {data?.status === "closed" && <p className="status-close">Closed</p>}
                                                        {data?.status === "open" && <p className="status-open">Open</p>}
                                                    </td>
                                                </motion.tr>
                                            )
                                        })
                                    }
                                </tbody>
                            </Table>
                        }
                        
                        { !isLoading && cvesList && !scanNotFound && cvesList?.results?.length > 0 &&
                            <Pagination
                                className="pagination-bar"
                                currentPage={currentPage}
                                pageSize={currentPageSize}
                                totalCount={cvesList?.meta?.total_results}
                                onPageChange={setCurrentPageandRecordSize}
                            />
                        }
                        {
                            !isLoading && !scanNotFound && cvesList !== "" && cvesList?.results?.length === 0 && 
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                <NoDataFound noDataText={"No Record Found"}/>
                            </motion.div>
                        }
                        {
                            !isLoading && scanNotFound && 
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                <NoDataFound noDataText={cvesList?.description}/>
                            </motion.div>
                        }
                    </div>
                </div>                
            </div> 
        </>        
    )
}


export default CriticalVulnerabilities;